import { useEffect, useRef, useState } from "react";
import {
  bumpUnit, formatOffset, formatTimecode, framesToParts, partsToFrames,
  timecodeAt, timecodeSpec,
  type TimecodeParts, type TimecodeUnit,
} from "@infoflyt/shared/timecode";
import type { Oekt } from "../state/session";
import * as nettlogg from "../state/nettlogg";
import { supabase } from "../state/supabase";
import { Wheel } from "./Wheel";

/**
 * Setter produksjonens tidskode, fra nettflata.
 *
 * ========  SAMME ARK SOM I APPEN  ========
 *
 * Brukeren 03.11.2026: «Det var ikke en veldig intuitiv måte å sette
 * tidskoden. Se på den vi har i appen, og kopier den. Jeg vil fortsatt
 * kunne velge og skrive inn om jeg heller vil det enn å dra i hjulet.»
 *
 * Første utgave var ett tekstfelt. Det er riktig for en som sitter med et
 * tastatur og vet hva hen skal skrive — men det er ikke måten dette gjøres
 * på. Man stiller fire tall mot en boks man leser av, og bekrefter i det
 * den treffer verdien. Hjulene er den gesten; feltet under er for den som
 * heller taster.
 *
 * BEGGE SKRIVER DET SAMME TALLET. Hjulene og feltet styrer én verdi —
 * `target` — så det finnes ingen tilstand der de to kan si hver sin ting.
 *
 * ========  AVLESNINGEN TAS I TRYKKET  ========
 *
 * Tiden det tar å stille fire tall er tid tidskoden har gått videre. Derfor
 * regnes ankeret mot klokka i det Bekreft treffes, ikke mot klokka da
 * vinduet åpnet — samme rettelse feltappens ark fikk 02.11.2026.
 *
 * ========  SERVEREN SETTER RESTEN  ========
 *
 * `sett_tidskode` regner ut forfatteren og setter `kind = 'jam'` selv.
 * Klienten påstår ingenting den kan tas i å ta feil om — se CLAUDE.md og
 * migrasjon 20261103090000.
 */

const UNITS: readonly { unit: TimecodeUnit; label: string }[] = [
  { unit: "hours", label: "TT" },
  { unit: "minutes", label: "MM" },
  { unit: "seconds", label: "SS" },
  { unit: "frames", label: "FF" },
];

const p2 = (n: number): string => String(n).padStart(2, "0");

export function Tidskodevindu({ oekt, anker, paaLukk, paaSatt }: {
  readonly oekt: Oekt;
  /** Produksjonens jam for dagen, eller null. Brukes til differansen. */
  readonly anker: {
    readonly tcFrames: number;
    readonly monotonicMs: number;
    readonly bootId: string;
    readonly wallUtcMs: number;
  } | null;
  paaLukk(): void;
  /** Kalles når ankeret er satt, så toppstripa kan lese det på nytt. */
  paaSatt(): void;
}): JSX.Element {
  const spec = timecodeSpec(oekt.production.framerate, oekt.production.dropFrame);

  /** Hva som står nå, regnet av produksjonens jam. Null uten jam. */
  const [naa, setNaa] = useState<number | null>(null);
  useEffect(() => {
    const les = (): void => {
      setNaa(anker === null ? null : timecodeAt(
        { ...anker, framerate: oekt.production.framerate,
          dropFrame: oekt.production.dropFrame, source: "manual" },
        { monotonicMs: 0, bootId: "nett", wallUtcMs: Date.now() },
        spec,
      ).frames);
    };
    les();
    /* Ti ganger i sekundet, som toppstripa: differansen under skal være
       sann i det man trykker. */
    const id = window.setInterval(les, 100);
    return () => { window.clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anker]);

  /**
   * Verdien som stilles inn.
   *
   * Starter på et helt sekund: man sikter mot et sekundskifte, og
   * frametallet på en boks er ikke lesbart i fart. Uten jam starter den på
   * klokka — det er den eneste referansen som finnes da.
   */
  const [target, setTarget] = useState(() => {
    /*
     * Regnet av ANKERET her, ikke av `naa`: tilstanden settes i foerste
     * render, og `naa` kommer foerst fra effekten under. Uten dette sto
     * hjulene paa klokka selv om produksjonen hadde en jam.
     */
    const fra = anker === null
      ? ((): number => {
        const d = new Date();
        return (d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds())
          * spec.rate.nominal;
      })()
      : timecodeAt(
        { ...anker, framerate: oekt.production.framerate,
          dropFrame: oekt.production.dropFrame, source: "manual" },
        { monotonicMs: 0, bootId: "nett", wallUtcMs: Date.now() },
        spec,
      ).frames;
    /* FF staar paa 00: man sikter mot et helt sekund, og frametallet paa en
       boks er ikke lesbart i fart. */
    return fra - (fra % spec.rate.nominal);
  });
  const [active, setActive] = useState<TimecodeUnit>("seconds");
  const [jobber, setJobber] = useState(false);
  const [feil, setFeil] = useState("");

  /**
   * Feltene, slik at ett kan sende fokus til det neste.
   *
   * «Naar et er fylt ut, saa hopper den automatisk til neste felt» —
   * brukeren 03.11.2026. Det er fire tall man leser av en boks i ett
   * strekk, og en haand som maa flytte seg mellom hvert av dem mister
   * strekket.
   */
  const felt = useRef<Partial<Record<TimecodeUnit, HTMLInputElement | null>>>({});
  const neste = (unit: TimecodeUnit): void => {
    const i = UNITS.findIndex((u) => u.unit === unit);
    const nesteEnhet = UNITS[i + 1]?.unit;
    if (nesteEnhet === undefined) return;
    const el = felt.current[nesteEnhet];
    if (el === undefined || el === null) return;
    el.focus();
    el.select();
  };

  /** Setter ETT av de fire tallene, uten aa roere de andre. */
  const settEnhet = (unit: TimecodeUnit, verdi: number): void => {
    const p = framesToParts(target, spec);
    const neste: TimecodeParts = { ...p, [unit]: verdi };
    try {
      setTarget(partsToFrames(neste, spec));
    } catch {
      /* Drop-frame hopper over noen framenumre. Da staar verdien som den
         var — hjulet viser fortsatt et tall som finnes. */
    }
  };

  const parts = framesToParts(target, spec);
  const values: Record<TimecodeUnit, number> = {
    hours: parts.hours, minutes: parts.minutes,
    seconds: parts.seconds, frames: parts.frames,
  };
  const caps: Record<TimecodeUnit, number> = {
    hours: 24, minutes: 60, seconds: 60, frames: spec.rate.nominal,
  };

  const sett = async (): Promise<void> => {
    if (supabase === null) { setFeil("Ingen forbindelse til basen."); return; }
    setJobber(true);
    setFeil("");
    /* Avlesningen tas HER, i trykket. Se komponentens topp. */
    const lest = nettlogg.naa();
    const { error } = await supabase.rpc("sett_tidskode", {
      p_produksjon: oekt.production.id,
      p_dag: oekt.shootDay,
      p_frames: target,
      p_framerate: oekt.production.framerate,
      p_drop: oekt.production.dropFrame,
      p_monotonic: lest.monotonicMs,
      p_boot: lest.bootId,
      p_wall: new Date(lest.wallUtcMs).toISOString(),
    });
    setJobber(false);
    if (error !== null) { setFeil(error.message); return; }
    paaSatt();
    paaLukk();
  };

  return (
    <div className="vindu" role="dialog" aria-modal="true" aria-label="Sett tidskoden">
      <div className="vindu__teppe" aria-hidden="true" onClick={paaLukk} />
      <div className="vindu__boks">
        <header className="vindu__topp">
          <span className="vindu__tittel">Sett tidskoden</span>
          <span className="vindu__steg mono">
            {naa === null ? "ingen jam i dag" : `Nå ${formatTimecode(naa, spec)}`}
          </span>
        </header>

        <div className="tcvindu__hjul">
          {UNITS.map(({ unit, label }) => {
            const v = values[unit];
            const cap = caps[unit];
            return (
              <Wheel
                key={unit}
                label={label}
                size="small"
                value={p2(v)}
                prev={p2((v + 1) % cap)}
                next={p2((v - 1 + cap) % cap)}
                prev2={p2((v + 2) % cap)}
                next2={p2((v - 2 + cap) % cap)}
                active={active === unit}
                onFocus={() => setActive(unit)}
                onStep={(dir) => { setTarget((t) => bumpUnit(t, unit, dir, spec)); }}
                /* Tallet er feltet. To siffer fyller det, og da hopper
                   fokus videre. */
                skrivbar
                tak={cap}
                onSkrevet={(v) => settEnhet(unit, v)}
                onNeste={() => neste(unit)}
                settFelt={(el) => { felt.current[unit] = el; }}
              />
            );
          })}
        </div>

        {/*
          * Framerate og differanse. Ikke et felt: TALLENE er feltene nå —
          * se hjulene over og `skrivbar` i Wheel.tsx.
          */}
        <p className="merknad tcvindu__under">
          {spec.rate.name} fps{spec.dropFrame ? " drop-frame" : ""}
          {naa === null ? "" : ` · differanse ${formatOffset(target - naa, spec)}`}
        </p>

        <p className="merknad">
          Still inn verdien som står på boksen, og bekreft i det den treffer
          den. Tidskoden gjelder hele produksjonen på denne opptaksdagen —
          alle flatene leser den, og postene som alt er skrevet flytter seg
          med.
        </p>

        {feil === "" ? null : (
          <p className="synk__melding synk__melding--feil">{feil}</p>
        )}

        <footer className="vindu__bunn">
          <button type="button" className="synk__knapp" onClick={paaLukk}>
            Avbryt
          </button>
          <button
            type="button"
            className="synk__knapp synk__knapp--hoved"
            disabled={jobber}
            onClick={() => { void sett(); }}
          >
            {jobber ? "Setter …" : "Bekreft"}
          </button>
        </footer>
      </div>
    </div>
  );
}
